import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
});

// JWT interceptor - add token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// 401 interceptor - redirect to login on auth failure
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config.url?.includes("/auth/")) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// ---- Projects ----

export const getProjects = async () => {
  const res = await api.get("/projects");
  return res.data;
};

export const createProject = async (name, description) => {
  const res = await api.post("/projects", { name, description });
  return res.data;
};

export const getProject = async (id) => {
  const res = await api.get(`/projects/${id}`);
  return res.data;
};

export const uploadPhotos = async (projectId, files) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append("files", file);
  });
  const res = await api.post(`/projects/${projectId}/upload`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

export const updatePhoto = async (projectId, photoId, data) => {
  const res = await api.put(`/projects/${projectId}/photos/${photoId}`, data);
  return res.data;
};

export const uploadAudio = async (projectId, photoId, audioBlob) => {
  const formData = new FormData();
  formData.append("file", audioBlob, "recording.webm");
  const res = await api.post(`/projects/${projectId}/photos/${photoId}/audio`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

export const analyzePhotos = async (projectId) => {
  const res = await api.post(`/projects/${projectId}/analyze_photos`);
  return res.data;
};

export const generateLayout = async (projectId, formData) => {
  const res = await api.post(`/projects/${projectId}/generate_layout`, formData);
  return res.data;
};

export const generateCover = async (projectId, formData) => {
  const res = await api.post(`/projects/${projectId}/generate_cover`, formData);
  return res.data;
};

export const updatePage = async (projectId, pageNumber, assignedPhotoIds) => {
  const res = await api.put(`/projects/${projectId}/pages/${pageNumber}`, { assigned_photo_ids: assignedPhotoIds });
  return res.data;
};

// ---- Share Links ----

export const getShareLinks = async (projectId) => {
  const res = await api.get(`/projects/${projectId}/shares`);
  return res.data;
};

export const createShareLink = async (projectId, data) => {
  const res = await api.post(`/projects/${projectId}/share`, data);
  return res.data;
};

export const updateShareLink = async (projectId, shareId, data) => {
  const res = await api.put(`/projects/${projectId}/shares/${shareId}`, data);
  return res.data;
};

export const deleteShareLink = async (projectId, shareId) => {
  const res = await api.delete(`/projects/${projectId}/shares/${shareId}`);
  return res.data;
};

// ---- Selections ----

export const getProjectSelections = async (projectId) => {
  const res = await api.get(`/projects/${projectId}/selections`);
  return res.data;
};

export const getSelection = async (selectionId) => {
  const res = await api.get(`/selections/${selectionId}`);
  return res.data;
};

export const updateSelectionStatus = async (selectionId, status) => {
  const res = await api.put(`/selections/${selectionId}`, { status });
  return res.data;
};

export const exportSelectionCSV = async (selectionId) => {
  const res = await api.get(`/selections/${selectionId}/export`, { responseType: "blob" });
  return res.data;
};

// ---- Clients ----

export const getClients = async () => {
  const res = await api.get("/clients");
  return res.data;
};

export const getClient = async (id) => {
  const res = await api.get(`/clients/${id}`);
  return res.data;
};

export const createClient = async (data) => {
  const res = await api.post("/clients", data);
  return res.data;
};

export const updateClient = async (id, data) => {
  const res = await api.put(`/clients/${id}`, data);
  return res.data;
};

export const deleteClient = async (id) => {
  const res = await api.delete(`/clients/${id}`);
  return res.data;
};

// ---- Orders ----

export const getOrders = async (params) => {
  const res = await api.get("/orders", { params });
  return res.data;
};

export const getOrder = async (id) => {
  const res = await api.get(`/orders/${id}`);
  return res.data;
};

export const createOrder = async (data) => {
  const res = await api.post("/orders", data);
  return res.data;
};

export const updateOrder = async (id, data) => {
  const res = await api.put(`/orders/${id}`, data);
  return res.data;
};

// ---- Watermark ----

export const getWatermarkPreview = async () => {
  const res = await api.get("/watermark/preview");
  return res.data;
};

export const updateWatermarkSettings = async (data) => {
  const res = await api.put("/watermark/settings", data);
  return res.data;
};

export const uploadWatermarkImage = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const res = await api.post("/watermark/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

// ---- Pricing ----

export const getProjectPricing = async (projectId) => {
  const res = await api.get(`/projects/${projectId}/pricing`);
  return res.data;
};

export const updateProjectPricing = async (projectId, data) => {
  const res = await api.put(`/projects/${projectId}/pricing`, data);
  return res.data;
};

// ---- Stats ----

export const getProjectStats = async (projectId) => {
  const res = await api.get(`/projects/${projectId}/stats`);
  return res.data;
};

// ---- Comments ----

export const getProjectComments = async (projectId) => {
  const res = await api.get(`/projects/${projectId}/comments`);
  return res.data;
};

export const deleteComment = async (commentId) => {
  const res = await api.delete(`/comments/${commentId}`);
  return res.data;
};

// ---- Branding ----

export const getBranding = async () => {
  const res = await api.get("/branding");
  return res.data;
};

export const updateBranding = async (data) => {
  const res = await api.put("/branding", data);
  return res.data;
};

export const uploadBrandingLogo = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const res = await api.post("/branding/logo", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

// ---- Payments ----

export const getPaymentConfig = async () => {
  const res = await api.get("/payments/config");
  return res.data;
};

export const updatePaymentConfig = async (data) => {
  const res = await api.put("/payments/config", data);
  return res.data;
};

export const createCheckoutSession = async (orderId) => {
  const res = await api.post(`/orders/${orderId}/checkout`);
  return res.data;
};

// ---- Seed ----

export const seedData = async () => {
  const res = await api.post("/seed");
  return res.data;
};

export default api;
